import IosShareOutlined from "@mui/icons-material/IosShareOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useShowroomData } from "../../hooks/use-build-showroom-data";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { SharingLinkField } from "./sharing-link";
import { uuidToShortId } from "../../../../utils/uuid-to-short-id";
import { useToken } from "../../../../server/auth/use-token";
import { authHeader } from "../../../../utils/auth-header";
import { showroomClient } from "../../../showroom/client";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { SupabaseMomentService } from "../../server/supabase-moment-service";

export const SharingLinkToolbarButton: FC = () => {
  const { open, handleOpen, handleClose } = useDialog();

  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: ["showroom_key", "id", "status"] });
  const [showroomKey, momentId, status] = getValues(["showroom_key", "id", "status"]);

  const { data: showroomData, loading: showroomLoading } = useShowroomData();
  const [generating, setGenerating] = useState(false);
  const getToken = useToken();

  const handleGenerate = useSupabaseCallback(
    async ({ supabase }) => {
      if (!showroomData) {
        toast.error("Could not build showroom data");
        return;
      }
      setGenerating(true);

      const shortId = await uuidToShortId(momentId);

      const showRoomResult = await showroomClient.POST("/publish", {
        body: { short_id: shortId, preview_html: showroomData.body, subject: showroomData.subject },
        ...authHeader(await getToken()),
      });

      if (showRoomResult.error !== undefined) {
        setGenerating(false);
        toast.error("Could not generate sharing link");
        return;
      }
      const published = showRoomResult.data;
      if (!published) {
        setGenerating(false);
        toast.error("Could not generate sharing link");
        return;
      }
      const publishedKey = published.key ?? shortId;

      const momentUpdate = await new SupabaseMomentService(supabase).update(momentId, {
        showroom_key: publishedKey,
      });

      if (momentUpdate.error !== null) {
        setGenerating(false);
        toast.error("Could not generate sharing link");
        return;
      }

      setGenerating(false);
      setValue("showroom_key", publishedKey);
    },
    [getToken, momentId, setValue, showroomData],
  );

  return (
    <>
      <Button
        data-analytics-id="moment-form-get-sharing-link-button"
        variant="outlined"
        sx={{ ml: 1 }}
        startIcon={<IosShareOutlined />}
        onClick={handleOpen}
        disabled={status === "draft"}
      >
        Get sharing link
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ px: 3, pt: 3 }}>Get sharing link</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={3}>
            <Typography variant="body2" fontSize="14px">
              Generate a shareable link and QR code to the <b>most recently published version of</b>{" "}
              this Communication. Employees will be able to access an online version without logging
              in.
            </Typography>
            <Typography variant="body2" fontSize="14px">
              In the online version:
            </Typography>
            <Typography variant="body2" fontSize="14px">
              <ul style={{ paddingLeft: "1em" }}>
                <li>
                  Content will remain in the state it was when published - it cannot be edited
                </li>
                <li>
                  Merge tags will be replaced with default values, which will not be personalized to
                  recipients
                </li>
                <li>Dynamic content blocks will be hidden</li>
                <li>
                  <b>
                    Communication will be viewable by anyone with the link, without needing to log
                    in.
                  </b>
                </li>
              </ul>
            </Typography>
            <Typography variant="body2" fontSize="14px">
              You can also insert the link directly into another Communication.
            </Typography>
            {showroomKey ? (
              <SharingLinkField />
            ) : (
              <Button
                data-analytics-id="moment-form-generate-sharing-link-button"
                variant="contained"
                loading={generating}
                disabled={generating || showroomLoading || !showroomData}
                onClick={() => void handleGenerate()}
                sx={{ alignSelf: "center" }}
              >
                Generate sharing link &amp; QR code
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-analytics-id="moment-form-get-sharing-link-cancel-button"
            onClick={handleClose}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
